/**
 * Speech Therapy Discharge Note — options + empty payload.
 * Common header (1–6) via StNotesCommonHeader; clinical sections 7–19
 * per ST Discharge Summary mockups.
 */

import { emptyStCommonHeader } from "./stNotesCommonHeaderUtils";

/** Row-major so a 2-column grid matches screenshot columns. */
export const DISCHARGE_REASON_OPTIONS = [
  "Communication Goals Met",
  "Swallowing Goals Met",
  "Maximum Functional Potential Achieved",
  "Patient Independent With Strategies",
  "Patient Declined Further Therapy",
  "Patient Hospitalized",
  "Patient Transferred to Facility",
  "Physician Discontinued Therapy",
  "Insurance Authorization Ended",
  "Non-Compliance",
  "Medical Status Changed",
];

export const SWALLOWING_STATUS_OPTIONS = [
  "Safe w/ Precautions",
  "Safe - Regular",
  "Unsafe - NPO",
];

export const SPEECH_INTELLIGIBILITY_OPTIONS = [
  "75-100%",
  "50-74%",
  "Below 50%",
];

export const COMMUNICATION_EXPRESSION_OPTIONS = [
  "Functional",
  "Needs Assistance",
  "Limited",
];

export const COGNITIVE_MENTAL_OPTIONS = [
  "Alert/Oriented",
  "Mild Impairment",
  "Significant Impairment",
];

export const SPEECH_PRODUCTION_STATUS_OPTIONS = [
  "Within Functional Limits",
  "Improved",
  "Requires Assistance",
  "Impaired",
];

export const LANGUAGE_STATUS_OPTIONS = [
  "Independent",
  "Improved",
  "Requires Cueing",
  "Impaired",
];

/** Row-major 3-column screenshot. */
export const COGNITIVE_AREA_OPTIONS = [
  "Memory",
  "Attention",
  "Problem Solving",
  "Reasoning",
  "Safety Awareness",
  "Executive Function",
];

export const SOLIDS_CONSISTENCY_OPTIONS = [
  "Regular",
  "Soft & Bite-Sized",
  "Minced & Moist",
  "Pureed",
  "NPO",
];

export const LIQUIDS_CONSISTENCY_OPTIONS = [
  "Thin",
  "Slightly Thick",
  "Moderately Thick",
  "Extremely Thick",
  "NPO",
];

/** Row-major 3-column screenshot. */
export const SWALLOW_PRECAUTION_OPTIONS = [
  "Chin Tuck",
  "Alternating Liquids/Solids",
  "Double Swallow",
  "Upright Positioning (90°)",
  "Small Bites/Sips",
  "Supraglottic Swallow",
];

export const FUNCTIONAL_ABILITY_ROWS = [
  "Communicating Needs",
  "Following Directions",
  "Conversation Skills",
  "Safety Communication",
];

export const FUNCTIONAL_ABILITY_OPTIONS = ["Independent", "Needs Assistance"];

export const HEARING_STATUS_OPTIONS = [
  "Within Functional Limits",
  "Impaired (No Aids)",
  "Aids Used (Unilateral)",
  "Aids Used (Bilateral)",
  "Unable to Assess",
];

export const ORAL_MOTOR_OPTIONS = [
  "Symmetrical",
  "Weakness Observed",
  "Severe Atrophy",
];

export const PAIN_IMPACT_OPTIONS = ["None", "Minimal", "Significant"];

export const ST_GOAL_ROWS = [
  "Speech Goals",
  "Language Goals",
  "Cognitive Goals",
  "Swallowing Goals",
  "Patient/Caregiver Education Goals",
];

export const GOAL_STATUS_OPTIONS = ["Met", "Partially Met", "Not Met"];

/** Row-major so a 3-column grid matches screenshot columns. */
export const ST_INTERVENTION_OPTIONS = [
  "Speech Production Training",
  "Language Therapy",
  "Cognitive Communication Training",
  "Memory Strategies",
  "Problem Solving Training",
  "Swallowing Exercises",
  "Diet Modification Training",
  "Aspiration Prevention Education",
  "Compensatory Strategy Training",
  "Caregiver Training",
];

export const EDUCATION_TOPIC_OPTIONS = [
  "Safe Swallowing Precautions",
  "Diet Modification",
  "Communication Strategies",
  "Family Training",
];

export const HEP_COMPLIANCE_OPTIONS = [
  "Excellent - Fully Compliant",
  "Good - Mostly Compliant",
  "Fair - Inconsistent",
  "Poor - Non-Compliant",
  "Not Applicable / Not Issued",
];

export const DISCHARGE_DISPOSITION_OPTIONS = [
  "Home (Independent)",
  "Home (With Caregiver)",
  "Assisted Living",
  "Skilled Nursing Facility",
  "Hospital / Acute Care",
  "Hospice",
  "Other",
];

export const ST_DISCHARGE_ARRAY_KEYS = [
  "dischargeReasons",
  "cognitiveAreasAssessed",
  "swallowPrecautions",
  "interventionsCompleted",
  "educationTopics",
];

export const toggleInList = (list, item) => {
  const arr = Array.isArray(list) ? list : [];
  return arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item];
};

export const today = () => new Date().toISOString().slice(0, 10);

export const emptyFunctionalAbility = () =>
  Object.fromEntries(FUNCTIONAL_ABILITY_ROWS.map((row) => [row, ""]));

export const mergeFunctionalAbility = (loaded) => {
  const src = loaded && typeof loaded === "object" ? loaded : {};
  return Object.fromEntries(
    FUNCTIONAL_ABILITY_ROWS.map((row) => [
      row,
      FUNCTIONAL_ABILITY_OPTIONS.includes(src[row]) ? src[row] : "",
    ]),
  );
};

export const emptyGoalStatuses = () =>
  Object.fromEntries(ST_GOAL_ROWS.map((goal) => [goal, ""]));

export const mergeGoalStatuses = (loaded) => {
  const src = loaded && typeof loaded === "object" ? loaded : {};
  return Object.fromEntries(
    ST_GOAL_ROWS.map((goal) => [
      goal,
      GOAL_STATUS_OPTIONS.includes(src[goal]) ? src[goal] : "",
    ]),
  );
};

export const emptyStDischargePayload = () => ({
  billingCode: "G0153",
  timeIn: "",
  timeOut: "",
  dischargeDate: today(),
  commonHeader: emptyStCommonHeader(),

  // —— 7. Reason for ST Discharge ——
  dischargeReasons: [],
  dischargeReasonOther: "",

  // —— 8. Subjective Report ——
  subjectiveProgress: "",

  // —— 9. Patient Status at Discharge ——
  swallowingStatus: "",
  speechIntelligibility: "",
  communicationExpression: "",
  cognitiveMentalStatus: "",

  // —— 10. Communication Assessment ——
  speechProductionStatus: "",
  speechProductionFindings: "",
  expressiveLanguageStatus: "",
  expressiveLanguageAssessment: "",
  receptiveLanguageStatus: "",
  receptiveLanguageAssessment: "",
  cognitiveAreasAssessed: [],
  cognitiveCurrentStatus: "",

  // —— 11. Swallowing & Diet ——
  solidsConsistency: "",
  liquidsConsistency: "",
  swallowPrecautions: [],

  // —— 12. Functional Communication ——
  functionalAbility: emptyFunctionalAbility(),
  functionalComments: "",

  // —— 13. Hearing & Oral Motor ——
  hearingStatus: "",
  oralMotorAssessment: "",

  // —— 14. Pain ——
  painLevel: null,
  painImpact: "",

  // —— 15. ST Goals Status ——
  goalStatuses: emptyGoalStatuses(),
  goalProgressSummary: "",

  // —— 16. Interventions ——
  interventionsCompleted: [],

  // —— 17. Education & HEP ——
  educationTopics: [],
  hepCompliance: "",

  // —— 18. Discharge Narrative & Plan ——
  dischargeNarrative: "",
  dischargeDisposition: "",
  followUpRecommendation: "",

  // —— 19. Physician / Care Team ——
  physicianCommunication: "",
});
